import { useEffect, useState } from "react";
import { Sparkles, Loader2, Wand2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AI_IMAGE_GEN_ENABLED } from "@/lib/featureFlags";

/**
 * One-click "draw an AAC symbol for every tile that's still showing just
 * an emoji." Calls the bulk-illustrate endpoint in batches of `MAX_PER_RUN`
 * so even big boards finish in a few minutes — caregivers can pause any time.
 */
const MAX_PER_RUN = 12;

export default function BulkIllustrate({ profileId }) {
  const [emojiOnly, setEmojiOnly] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [lastResults, setLastResults] = useState([]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const refreshCount = async () => {
    try {
      const { data } = await api.get(`/profiles/${profileId}/tiles`);
      const n = (data || []).filter((t) => !t.custom_image_b64 && !t.image_url).length;
      setEmojiOnly(n);
    } catch {
      setEmojiOnly(null);
    }
  };

  useEffect(() => {
    refreshCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const run = async () => {
    if (!emojiOnly || emojiOnly === 0) return;
    setRunning(true);
    setError("");
    setInfo("");
    setLastResults([]);
    setProgress({ done: 0, total: emojiOnly });

    let totalDrawn = 0;
    let remaining = emojiOnly;
    let allResults = [];

    while (remaining > 0 && running !== false) {
      try {
        const { data } = await api.post(
          `/profiles/${profileId}/bulk-illustrate`,
          { only_missing: true, max_tiles: MAX_PER_RUN }
        );
        totalDrawn += data.drawn || 0;
        allResults = allResults.concat(data.results || []);
        remaining = data.remaining_emoji_only ?? 0;
        setProgress({ done: totalDrawn, total: totalDrawn + remaining });
        setLastResults(allResults.slice(-MAX_PER_RUN));
        if ((data.drawn || 0) === 0) break; // safety: nothing drew, stop
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        break;
      }
    }
    setInfo(`Drew ${totalDrawn} custom symbols. ${remaining} left to do — run it again any time.`);
    setRunning(false);
    await refreshCount();
  };

  return (
    <section className="brutal-card bg-white p-5 mb-4" data-testid="bulk-illustrate-section">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-9 h-9 rounded-xl border-[3px] border-[color:var(--ink)] bg-[#A78BFA] text-white flex items-center justify-center">
          <Wand2 size={16} />
        </span>
        <div className="flex-1">
          <h2 className="display text-xl font-bold leading-tight flex items-center gap-2">
            Illustrate the whole board <Sparkles size={16} className="text-[color:var(--coral)]" />
          </h2>
          <p className="text-xs text-[color:var(--ink-soft)]">
            One tap draws a flat, AAC-style custom symbol for every emoji-only tile.
            Tiles that already have a photo are left alone.
          </p>
        </div>
      </div>

      <div className="brutal-card bg-[color:var(--paper-muted)] p-3 mb-3 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[12rem]">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[color:var(--ink-soft)]">Emoji-only tiles</div>
          <div className="display text-3xl font-extrabold mt-0.5">
            {emojiOnly === null ? "…" : emojiOnly}
          </div>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={!AI_IMAGE_GEN_ENABLED || running || emojiOnly === 0 || emojiOnly === null}
          className="pill-btn primary"
          data-testid={AI_IMAGE_GEN_ENABLED ? "bulk-illustrate-run" : "bulk-illustrate-coming-soon"}
          title={AI_IMAGE_GEN_ENABLED ? "" : "AI image generation is coming soon"}
        >
          {!AI_IMAGE_GEN_ENABLED ? (
            <>
              <Sparkles size={14} /> ✨ Coming soon
            </>
          ) : (
            <>
              {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {running ? "Drawing…" : emojiOnly === 0 ? "All done!" : `Illustrate ${emojiOnly} tiles`}
            </>
          )}
        </button>
      </div>

      {running && (
        <div className="mb-3" data-testid="bulk-illustrate-progress">
          <div className="text-sm text-[color:var(--ink-soft)] mb-1">
            Drawing… {progress.done}/{progress.total} (each symbol takes 5–15s)
          </div>
          <div className="w-full h-2 rounded-full bg-[color:var(--paper-muted)] border-[2px] border-[color:var(--ink)] overflow-hidden">
            <div
              className="h-full bg-[color:var(--coral)] transition-all"
              style={{ width: `${progress.total ? (100 * progress.done) / progress.total : 0}%` }}
            />
          </div>
        </div>
      )}

      {info && !running && (
        <div className="brutal-card bg-[#E6F8E1] p-2 text-sm mb-2" data-testid="bulk-illustrate-info">{info}</div>
      )}
      {error && (
        <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-2 text-sm mb-2" data-testid="bulk-illustrate-error">{error}</div>
      )}

      {lastResults.length > 0 && !running && (
        <details className="text-sm">
          <summary className="cursor-pointer text-[color:var(--ink-soft)]">Show last batch ({lastResults.length} tiles)</summary>
          <ul className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-1">
            {lastResults.map((r, i) => (
              <li key={i} className="flex items-center gap-1">
                <span aria-hidden>{r.ok ? "✅" : "⚠️"}</span>
                <span className="truncate">{r.label}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
